import { ReactNode, useState, Fragment } from 'react';

import {
	RewardScene,
	MemberSystemProps,
	VipLevel,
	AchieventCardProps,
	Privilege, AwardType, PrivilegeType
} from '@/types';
import { useReward } from '@/contexts/reward-context';
import { EventLayout } from '@/layouts/event-layout';
import { RewardSystem } from './reward-system';
import { getProgressPercentage, addCommas } from '@/utils';
import { EventTab } from '@/types';
import { Privileges } from '@/types/constants';
import { Controller, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ArrowRightIcon, RankIcon, StoreIcon } from './svg-icons';
import { CoinIcon } from '@/components/icons/coin-icon';
import { Swiper as SwiperType } from 'swiper';
import { RewardSbcTooltip } from './reward-sbc-tooltip';

export const EventPage = () => {
	const {
		planInfo,
		labels,
		enableCoinEventUpdate,
		onKafkaSet,
		platform,
		setRewardScene
	} = useReward();
	const [currentTab, setCurrentTab] = useState(EventTab.member);
	const isMember = currentTab === EventTab.member
	const [isShowTooltip, setIsShowTooltip] = useState(false);
	const [userGetPoint, setUserGetPoint] = useState(0);

	const clickTab = (entrance: string) => {
		onKafkaSet({
			type: 2,
			action: entrance === EventTab.member ? 3 : 4
		});
		setCurrentTab(entrance)
	}

	const visitRanking = () => {
		onKafkaSet({
			type: 2,
			action: 2
		});
		setRewardScene(RewardScene.Ranking);
	};

	const visitShop = () => {
		onKafkaSet({
			type: 2,
			action: 1
		});
		setRewardScene(RewardScene.Shop);
	};

	const currentRewardLevel = planInfo?.VipLevel as number;
	const currentRewardLevelName = planInfo?.VipLevels[currentRewardLevel].VipLevelName as string;
	const totalExp = planInfo?.VipLevels.reduce((exp, vipLevelInfo) => {
		if (planInfo?.VipLevel > vipLevelInfo.VipLevel) {
			exp += vipLevelInfo.LevelUpExp;
		}
		return exp;
	}, planInfo?.CurrentExp);

	return (
		<EventLayout scene={RewardScene.Home}>
			<div className="reward-head">
				<div className="reward-user">
					<div className="reward-user__info">
						<div className="reward-user__name">{planInfo?.UserName}</div>
						<div className="reward-user__level">{labels?.[currentRewardLevelName]}</div>
					</div>
					<div className="reward-badge-level" data-level={currentRewardLevel} data-highlight="true">
						<div className="reward-badge-level__img" />
					</div>
				</div>
				<div className="reward-aisle">
					{enableCoinEventUpdate ? <RewardSbcTooltip /> : null}

					<div className="reward-aisle__shop" onClick={visitShop}>
						<div className="reward-aisle__title">
							<StoreIcon />
							<div className="reward-aisle__text">{labels?.lbl_RPSabaShop}</div>
							<ArrowRightIcon />
						</div>
						<div className="reward-aisle__summary">
							<div className="reward-aisle__summary-title">{labels?.lbl_RPSabaCoins}</div>
							<div className="reward-coin">
								<CoinIcon />
								<div className="reward-coin__text">{addCommas(planInfo?.SabaCoin ?? 0)}</div>
							</div>
						</div>
					</div>
					<div className="reward-aisle__rank" onClick={visitRanking}>
						<div className="reward-aisle__title">
							<RankIcon />
							<div className="reward-aisle__text">{labels?.lbl_RPSabaRank}</div>
							<ArrowRightIcon />
						</div>
						<div className="reward-aisle__summary">
							<div className="reward-aisle__summary-title">{labels?.lbl_RPTotal}</div>
							<div className="reward-exp-point">
								<div className="reward-exp-point__icon">
									<i className="reward-iconcolor-exp-star" />
								</div>
								<div className="reward-exp-point__text">{addCommas(`${totalExp}`)}</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="reward-tabs">
				<div className="reward-tab" data-selected={isMember} onClick={() => clickTab(EventTab.member)}>
					<div className="reward-tab__title">{labels?.lbl_RPMemberSystem}</div>
				</div>
				<div className="reward-tab" data-selected={!isMember} onClick={() => clickTab(EventTab.task)}>
					<div className="reward-tab__title">{labels?.lbl_RPTaskPalace}</div>
				</div>
			</div>
			<MemberSystem
				isShow={currentTab === 'member'}
				isShowTooltip={isShowTooltip}
				userGetPoint={userGetPoint}
			/>
			<RewardSystem isShow={currentTab === 'task'} />
		</EventLayout>
	);
};

const MemberSystem = ({
	isShow,
	isShowTooltip,
	userGetPoint
}: MemberSystemProps) => {

	const { labels, setRewardScene, planInfo, platform, onKafkaSet } = useReward();
	const [cardSwiper, setCardSwiper] = useState<SwiperType>();
	const [privilegeSwiper, setPrivilegeSwiper] = useState<SwiperType>();

	const visitRules = () => {
		onKafkaSet({
			type: 2,
			action: 9
		});
		setRewardScene(RewardScene.Rule);
	};

	return (
		<div className="reward-content reward-content--level" data-show={isShow}>
			<Swiper
				id="swiper-reward-card"
				modules={[Controller]}
				spaceBetween="-21"
				slidesPerView="auto"
				initialSlide={planInfo?.VipLevel}
				centeredSlides={true}
				onSwiper={setCardSwiper}
				controller={{ control: privilegeSwiper }}
				resizeObserver={false}
				onSlideChange={(swiper) => {
					privilegeSwiper?.slideTo(swiper.activeIndex);
				}}
			>
				{
					planInfo?.VipLevels.map((level: VipLevel): ReactNode => {
						return (
							<SwiperSlide key={level.VipLevel}>
								<AchieventCard
									levelData={level}
									level={level.VipLevel}
									isShowTooltip={isShowTooltip}
									userGetPoint={userGetPoint}
								/>
							</SwiperSlide>
						)
					})
				}
			</Swiper>

			<div className="reward-benefit">
				<Swiper
					id="swiper-reward-benefit"
					modules={[Controller, EffectFade]}
					effect="fade"
					fadeEffect={{ crossFade: true }}
					allowTouchMove={false}
					onSwiper={setPrivilegeSwiper}
					controller={{ control: cardSwiper }}
				>
					{
						Privileges
							.filter((_, index: number) => {
								return index < (planInfo?.VipLevels?.length ?? 0);
							}).map((privilege: Privilege, index: number): ReactNode => {
								return (
									<SwiperSlide key={index}>
										<Benefit
											privilege={privilege}
											level={index}
										/>
									</SwiperSlide>
								)
							})
					}

				</Swiper>
			</div>

			<div className="reward-content__text-btn" onClick={visitRules}>
				<div className="reward-text">{labels?.lbl_RPRulesRegulation} &gt;</div>
			</div>
		</div>
	)
};

const AchieventCard = ({
	levelData,
	level,
	isShowTooltip,
	userGetPoint
}: AchieventCardProps) => {
	const { labels, planInfo, setRewardScene, platform, onKafkaSet } = useReward();
	const currentLevel = planInfo?.VipLevel as number;
	const isCurrentLevel = currentLevel == level;
	const isWild = level > (planInfo?.VipLevel as number);
	let currentExp = planInfo?.CurrentExp as number;

	if (currentLevel > level) {
		currentExp = levelData.LevelUpExp
	} else if (currentLevel < level) {
		currentExp = 0
	}

	const expPercentager = currentLevel > level ? 100 : currentLevel < level || isWild ? 0 : getProgressPercentage(currentExp, levelData.LevelUpExp);

	const visitRecord = () => {
		onKafkaSet({
			type: 2,
			action: 5
		});
		setRewardScene(RewardScene.ExpHistory);
	};

	return (
		<div className="reward-card" data-level={level} data-disabled={isWild}>
			<div className={'reward-card__status' + (isCurrentLevel ? ' reward-card__status--current' : '')}>
				<div className="reward-card__status-bg" />
				<span className="reward-text">
					{currentLevel > level ? labels?.lbl_RPUnlocked : isWild ? labels?.lbl_RPToBeUnlocked : labels?.lbl_CurrentLevel}
				</span>
			</div>
			<div className="reward-card__info">
				<div className="reward-card__title">
					<span className="reward-text">{labels?.[levelData.VipLevelName]}</span>
				</div>
				<div className="reward-card-progress" data-level={level}>
					<div className="reward-card-progress__exp-range">
						<i className="reward-iconcolor-exp-icon" />
						<div className="reward-card-progress__exp-state">
							{currentExp}
							{
								levelData.LevelUpExp ?
									` / ${levelData.LevelUpExp}` : null
							}
						</div>
					</div>
					<div className="reward-card-progress__base">
						<div className="reward-card-progress__exp" style={{ width: `${expPercentager}%` }}></div>
					</div>
				</div>
				{
					currentLevel == level ?
						<div className="reward-card__record" onClick={visitRecord}>
							<span className="reward-text">{labels?.lbl_RPExperienceLog} &gt;</span>
						</div> : null
				}
				<div className="reward-card__badge">
					<div className="reward-badge-level" data-level={level} data-highlight={isCurrentLevel}>
						<div className="reward-badge-level__img" />
					</div>
				</div>
			</div>
		</div>
	)
}

const Benefit = ({
	privilege,
	level
}: {
	privilege: Privilege;
	level: number;
}) => {
	const { labels, voucherCondition, onKafkaSet, platform } = useReward();
	const [privilegeType, setPrivilegeType] = useState<PrivilegeType>('SabaCoin');

	const handlePrivileageTypeClick = (type: PrivilegeType) => {
		let action = 6;
		switch (type) {
			case 'Rebate':
				action = 6;
				break;
			case 'SabaCoin':
				action = 7;
				break;
			case 'WeeklyAward':
				action = 8;
				break;
		}

		onKafkaSet({
			type: 2,
			action: action
		});

		setPrivilegeType(type);
	}

	const weeklyAwards = privilege.WeeklyAwards?.filter(award => {
		switch (award.AwardType) {
			case AwardType.FreeRiskBet:
				return voucherCondition.enableRiskFree;
			case AwardType.OddsBooster:
				return voucherCondition.enableOddsBooster;
			case AwardType.NicknameChange:
				return voucherCondition.enableNicknameChange;
			default:
				return true;
		}
	});

	const awardHtml = weeklyAwards?.reduce((html, award, index) => {
		if (index > 0) {
			html += '<br />';
		}
		const key =
			award.AwardType === AwardType.OddsBooster ? labels?.lbl_RPOddsBooster :
				award.AwardType == AwardType.NewRiskFreeBet ? labels?.lbl_RPRiskFreeBet : labels?.lbl_RPExchangeCashBack;
		html += `${key} * ${award.Amount}`;
		return html;
	}, '');

	const isFewSlides = ((privilege.JackPot && ['Mars', 'Galaxy'].includes(platform ?? '') ? 1 : 0) +
		(privilege.Rebate ? 1 : 0) +
		(privilege.SabaCoin ? 1 : 0) +
		(weeklyAwards && weeklyAwards.length && awardHtml ? 1 : 0)) < 3.5;

	return (
		<>
			<Swiper
				className="reward-benefit__tabs"
				slidesPerView={3.5}
				breakpoints={{
					768: {
						slidesPerView: 4,
					}
				}}
				data-few-slides={isFewSlides}
			>
				{
					<SwiperSlide>
						<div
							className="reward-benefit__tab"
							data-selected={privilegeType == 'SabaCoinRewardLevelInfo'}
							onClick={() => handlePrivileageTypeClick('SabaCoinRewardLevelInfo')}
						>
							<div className="reward-benefit__tab-btn">
								<div className="reward-benefit-icon">
									<i className="reward-iconcolor-sbc-flow"></i>
								</div>
								<div className="reward-benefit__text">{labels?.lbl_newturnover11}</div>
								<div className="reward-benefit__award">
								</div>
							</div>
						</div>
					</SwiperSlide>
				}
				{
					privilege.Rebate ?
						<SwiperSlide
							className="reward-benefit__tab"
							data-selected={privilegeType == 'Rebate'}
							onClick={() => handlePrivileageTypeClick('Rebate')}
						>
							<div className="reward-benefit__tab-btn">
								<div className="reward-benefit-icon">
									<i className="reward-iconcolor-percent" />
								</div>
								<div className="reward-benefit__text">{labels?.lbl_RPNLRebate}</div>
								<div className="reward-benefit__award">{`${privilege.Rebate}%`}</div>
							</div>
						</SwiperSlide> : null
				}
				{
					privilege.SabaCoin ?
						<SwiperSlide
							className="reward-benefit__tab"
							data-selected={privilegeType == 'SabaCoin'}
							onClick={() => handlePrivileageTypeClick('SabaCoin')}
						>
							<div className="reward-benefit__tab-btn">
								<div className="reward-benefit-icon">
									<i className="reward-iconcolor-check-full" />
								</div>
								<div className="reward-benefit__text">{labels?.lbl_RPGetSabaCoins}</div>
								<div className="reward-benefit__award">
									<div className="reward-coin">
										<CoinIcon />
										<div className="reward-coin__text">{`${privilege.SabaCoin}+`}</div>
									</div>
								</div>
							</div>
						</SwiperSlide> : null
				}
				{
					weeklyAwards && weeklyAwards.length && awardHtml ?
						<SwiperSlide
							className="reward-benefit__tab"
							data-selected={privilegeType == 'WeeklyAward'}
							onClick={() => handlePrivileageTypeClick('WeeklyAward')}
						>
							<div className="reward-benefit__tab-btn">
								<div className="reward-benefit-icon">
									<i className="reward-iconcolor-gift" />
								</div>
								<div className="reward-benefit__text">{labels?.lbl_RPWeeklyVoucher}</div>
								<div className="reward-benefit__award">
									<span dangerouslySetInnerHTML={{ __html: awardHtml }} />
								</div>
							</div>
						</SwiperSlide> : null
				}

			</Swiper >
			<BenefitDec
				privilegeType={privilegeType} 
				level={level} 
			/>
		</>
	)
}

const weeklyCommonLabels = [
	'lbl_RPVoucherRules20',
	'lbl_RPVoucherRules21',
	'lbl_RPVoucherRules22'
];

const weeklyLevelLabels: Record<number, Array<string>> = {
	3: ['lbl_RPVoucherRulesRubyLevel'],
	4: ['lbl_RPVoucherRulesDiamondLevel'],
	5: ['lbl_RPVoucherRulesMasterLevel', 'lbl_RPVoucherRulesExtra1'],
	6: ['lbl_RPVoucherRulesChallengerLevel', 'lbl_RPVoucherRulesExtra1'],
	7: ['lbl_RPVoucherRulesPeerlessKingLevel', 'lbl_RPVoucherRulesExtra1'],
	8: ['lbl_RPVoucherRulesLegendaryKingLevel', 'lbl_RPVoucherRulesExtra2']
};

const BenefitDec = ({
	privilegeType,
	level
}: {
	privilegeType: PrivilegeType;
	level: number;
}) => {
	const { labels } = useReward();

	const weeklyLabelsForLevel = weeklyLevelLabels[level] ?? [];
	const weeklyRuleLabels = [...weeklyLabelsForLevel, ...weeklyCommonLabels];

	const WeeklyAwardRules = weeklyRuleLabels.length > 0 ? (
		<>
			{weeklyRuleLabels.map((key, index) => (
				<Fragment key={key}>
					{labels?.[key]}
					{index < weeklyRuleLabels.length - 1 ? <br /> : null}
				</Fragment>
			))}
		</>
	) : null;

	return (
		<div className="reward-benefit__desc">
			<div className="reward-benefit__desc-title">
				{{
					'Rebate': labels?.lbl_RPRules2,
					'SabaCoin': labels?.lbl_RPRules3,
					'WeeklyAward': labels?.lbl_RPRules4,
					'SabaCoinRewardLevelInfo': labels?.lbl_newturnover12
				}[privilegeType]}
			</div>
			<div className="reward-benefit__desc-content">
				{{
					'Rebate': (
						<>
							{labels?.lbl_RPRebateAccumulates}<br />
							{labels?.lbl_RPRebateGiveaway}<br />
							{labels?.lbl_RPApplicable2}<br />
							{labels?.lbl_RPNoSupport}<br />
							{labels?.lbl_RPExplainSCrate}
						</>
					),
					'SabaCoin': (
						<>
							{labels?.lbl_RPCoinsRules1}<br />
							{labels?.lbl_RPCoinsRules2}<br />
							{labels?.lbl_RPCoinsRules3}
						</>
					),
					'WeeklyAward': WeeklyAwardRules,
					'SabaCoinRewardLevelInfo': (
						<>
							{labels?.lbl_newturnover13}
						</>
					)
				}[privilegeType]}
			</div>
		</div>
	);
};